import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Home } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useInquiryChat } from '@hooks/useInquiryChat';
import { markInquiryChatRead } from '@services/secureOperations';
import { ChatBubble, ChatInput } from '@components/ui';

interface InquiryChatPanelProps {
  inquiryId: string;
  className?: string;
}

const formatTime = (ms: number) =>
  ms ? new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Now';

/**
 * Shared 3-way chat UI for a property inquiry, reused by the landlord
 * (Inquiries modal) and the tenant (inquiry chat page). Attributes each
 * message to its sender since more than two people share the room.
 */
export const InquiryChatPanel: React.FC<InquiryChatPanelProps> = ({ inquiryId, className = '' }) => {
  const { currentUser } = useAuth();
  const { messages, sendMessage, loading, error } = useInquiryChat(inquiryId);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clear the unread badge for this participant when there are messages in view.
  useEffect(() => {
    if (!inquiryId || messages.length === 0) return;
    void markInquiryChatRead(inquiryId).catch((err) =>
      console.error('Error marking inquiry chat read:', err)
    );
  }, [inquiryId, messages.length]);

  const handleSend = async () => {
    if (!draft.trim() || sending) return;
    const text = draft.trim();
    setDraft('');
    setSending(true);
    try {
      await sendMessage(text);
    } catch (err) {
      console.error('Error sending inquiry message:', err);
      setDraft(text); // restore so the user doesn't lose their message
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-text-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading conversation…
          </div>
        ) : error ? (
          <div role="alert" className="text-center text-sm text-red-600 py-8">
            {error}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10">
            <Home className="w-10 h-10 text-primary-300 mx-auto mb-3" />
            <p className="text-sm text-text-secondary">
              Start the conversation about this property.
            </p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === currentUser?.uid;
            const senderLabel =
              message.senderName || (message.senderType === 'landlord' ? 'Landlord' : 'Tenant');
            return (
              <div key={message.id}>
                {!isOwn && (
                  <p className="text-[11px] text-text-secondary px-2 mb-0.5 flex items-center gap-1">
                    <span className="font-semibold">{senderLabel}</span>
                    {message.senderType === 'landlord' && (
                      <span className="px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-300 text-[9px] font-semibold border border-primary-100">
                        Landlord
                      </span>
                    )}
                  </p>
                )}
                <ChatBubble
                  message={message.text}
                  timestamp={formatTime(message.timestamp)}
                  isOwn={isOwn}
                  type="text"
                />
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t-2 border-gray-100 p-3 bg-white">
        <ChatInput value={draft} onChange={setDraft} onSend={handleSend} disabled={sending} />
      </div>
    </div>
  );
};

export default InquiryChatPanel;
